import { JsonPipe, KeyValuePipe, DatePipe } from '@angular/common';
import { Component, inject, OnInit, signal, WritableSignal } from '@angular/core';
import { FormBuilder, Validators, FormsModule, ReactiveFormsModule, FormControl, FormGroup } from '@angular/forms';
import { addMonths } from 'date-fns';
// Prime
import { ButtonModule } from 'primeng/button';
import { CalendarModule } from 'primeng/calendar';
import { CardModule } from 'primeng/card';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';

import { TOAST_ALERTS_TOKEN, ToastAlertsAbstractService, HttpCoreService } from '@dataclouder/ngx-core';
import { AppAuthClaims, ExpireDateOptions, PermissionsOptions, PermissionType, PlanOptions, RolOptions, RolType, StatusOptions } from '@dataclouder/app-auth';

import { PermissionNamePipe } from './permision-name.pipe';
import { UserBaseService } from '../services/user-base.service';

type InputClaim = {
  type: string;
  expMonths: number | null;
  exp: Date | null;
  num?: number;
};

const Endpoints = {
  Claims: 'api/users/admin/claims',
  DeleteUser: 'api/users/admin/user', // api/admin/user/{email}
};

@Component({
  selector: 'app-admin-user',
  templateUrl: './admin-user.component.html',
  styleUrls: ['./admin-user.component.scss'],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    InputTextModule,
    ButtonModule,
    DropdownModule,
    MultiSelectModule,
    JsonPipe,
    PermissionNamePipe,
    CalendarModule,
    KeyValuePipe,
    CardModule,
    DatePipe,
  ],
})
export class AdminUserComponent implements OnInit {
  ngOnInit(): void {
    // throw new Error('Method not implemented.');
  }
  private readonly formBuilder = inject(FormBuilder);
  private readonly httpService = inject(HttpCoreService);
  private toastrService = inject<ToastAlertsAbstractService>(TOAST_ALERTS_TOKEN);
  private readonly userService = inject(UserBaseService);

  public readonly userType = PermissionType;
  public readonly permissions = PermissionType;
  public readonly permissionsOptions = PermissionsOptions;
  public readonly rolOptions = RolOptions;
  public readonly expireDateOptions = ExpireDateOptions;
  public readonly planOptions = PlanOptions;
  public readonly statusOptions = StatusOptions;

  public deleteUserForm: WritableSignal<string> = signal('');
  public userClaims: WritableSignal<AppAuthClaims | null> = signal(null);
  public permissionInput: WritableSignal<InputClaim> = signal({ type: '', expMonths: null, exp: null, num: null });
  public rolInput: WritableSignal<InputClaim> = signal({ type: '', expMonths: null, exp: null });
  public planInput: WritableSignal<InputClaim> = signal({ type: '', expMonths: null, exp: null });
  public existingUser = signal(false);

  // la guía muestra que es mejor evitar tipar https://blog.angular-university.io/angular-typed-forms/ y si posicionamos formGroup ya sabe el tipo
  // Despues de muchas modificaciones me di cuenta que no era neceario un formGroup, ya ni modos es mas facil un objeto con las propiedade de AppAuthClaims, solo si avanza a algo más complicado valdria la pena
  public formGroup: FormGroup<{
    email: FormControl<string>; // Made email non-nullable in form control
    plan: FormGroup<{
      type: FormControl<string | null>;
      exp: FormControl<Date | null>;
    }>;
    permissions: FormGroup; // Controls added dynamically
    roles: FormGroup; // Controls added dynamically
    status: FormControl<string | null>;
  }> = this.formBuilder.group({
    email: new FormControl('', { validators: [Validators.required, Validators.email], nonNullable: true }),
    plan: this.formBuilder.group({
      type: new FormControl<string | null>(null),
      exp: new FormControl<Date | null>(null),
    }),
    permissions: this.formBuilder.group({}),
    roles: this.formBuilder.group({}),
    status: new FormControl<string | null>(null),
  });

  public resetForm(): void {
    // Clear dynamically added controls from roles and permissions
    Object.keys((this.formGroup.get('roles') as FormGroup).controls).forEach((key) => {
      (this.formGroup.get('roles') as FormGroup).removeControl(key);
    });
    Object.keys((this.formGroup.get('permissions') as FormGroup).controls).forEach((key) => {
      (this.formGroup.get('permissions') as FormGroup).removeControl(key);
    });

    // Reset specific form groups/controls
    this.formGroup.get('plan')?.reset({ type: null, exp: null });
    this.formGroup.get('status')?.reset(null);
    this.formGroup.get('email')?.reset('');

    // Reset signals
    this.userClaims.set(null);
    this.existingUser.set(false);
  }

  // Define a type for the permission object, make num optional in control
  private addPermission(permission: PermissionType, permissionObj: { exp: Date | null; num?: number | null }): void {
    const permissionsGroup = this.formGroup.get('permissions') as FormGroup;
    const control = new FormControl<{ exp: Date | null; num?: number | null }>(permissionObj);
    if (permissionsGroup.get(permission)) {
      // Cast to specific control type before setting value
      (permissionsGroup.controls[permission] as FormControl<{ exp: Date | null; num?: number | null }>).setValue(permissionObj);
    } else {
      permissionsGroup.addControl(permission, control);
    }
  }

  private addRolControl(rol: RolType, expireDate: Date | string | null): void {
    const rolesGroup = this.formGroup.get('roles') as FormGroup;
    const control = new FormControl<Date | string | null>(expireDate);
    if (rolesGroup.get(rol)) {
      // Cast to specific control type before setting value
      (rolesGroup.controls[rol] as FormControl<Date | string | null>).setValue(expireDate);
    } else {
      rolesGroup.addControl(rol, control);
    }
    console.log('addRolControl', rolesGroup.value);
  }

  // Type controlKey as string
  public removeControl(controlName: 'permissions' | 'roles', controlKey: string): void {
    const group = this.formGroup.get(controlName) as FormGroup;
    if (group) {
      group.removeControl(controlKey as PermissionType | RolType); // Cast based on context if needed, or keep general
      group.updateValueAndValidity();
    }
  }

  public addPermissionFormGroup(): void {
    const currentPermissionInput = this.permissionInput();
    const permissionObj: { exp: Date | null; num?: number | null } = { exp: currentPermissionInput.exp };
    if (currentPermissionInput.num != null) {
      permissionObj['num'] = currentPermissionInput.num;
    }
    this.addPermission(currentPermissionInput.type as PermissionType, permissionObj);
    this.permissionInput.set({ type: '', expMonths: null, exp: null, num: null }); // Reset signal
  }

  public addRolFormGroup(): void {
    const currentRolInput = this.rolInput();
    this.addRolControl(currentRolInput.type as RolType, currentRolInput.exp);
    this.rolInput.set({ type: '', expMonths: null, exp: null }); // Reset signal
  }

  public async getUserClaims() {
    // this.resetForm();

    const emailControl = this.formGroup.get('email');
    if (!emailControl || !emailControl.value) {
      // Check if emailControl.value is null or empty
      this.toastrService.error({ subtitle: 'Email is required.', title: 'Error' });
      return;
    }
    const email = emailControl.value.toLowerCase();
    emailControl.setValue(email);

    this.existingUser.set(false);

    try {
      const claims = await this.httpService.get<AppAuthClaims>(`${Endpoints.Claims}/${email}`);
      this.userClaims.set({ ...claims });

      this.toastrService.success({ subtitle: `Se obtuvieron los datos del usuario ${email}`, title: 'Éxito' });

      this.existingUser.set(true);

      if (claims.permissions) {
        Object.entries(claims.permissions).forEach(([key, value]) => {
          this.addPermission(key as PermissionType, value);
        });
      }

      if (claims.roles) {
        Object.entries(claims.roles).forEach(([key, value]) => {
          this.addRolControl(key as RolType, value || null);
        });
      }

      if (claims.plan) {
        const expiryDate = claims.plan.exp && typeof claims.plan.exp === 'number' ? new Date(claims.plan.exp) : null;
        this.formGroup.get('plan')?.setValue({ type: claims.plan.type, exp: expiryDate });
      }

      // Set status value from claims
      if (claims.status) {
        this.formGroup.get('status')?.setValue(claims.status as any);
      }
    } catch (error) {
      this.toastrService.error({ subtitle: `Error fetching user claims for ${email}`, title: 'Error' });
      console.error('Error fetching user claims:', error);
      this.existingUser.set(false);
      this.userClaims.set(null);
    }

    // this.resetForm();
  }

  public changePlan() {
    const currentPlanInput = { ...this.planInput() }; // Clone to avoid direct mutation if calculateExpireDate mutates
    this.calculateExpireDate(currentPlanInput); // Pass the cloned object

    this.formGroup.get('plan')?.setValue({
      type: currentPlanInput.type,
      exp: currentPlanInput.exp,
    });
    // Optionally, update the signal if calculateExpireDate's changes should be reflected back
    this.planInput.set(currentPlanInput);
  }

  public async updateUser() {
    if (this.formGroup.invalid) {
      this.toastrService.error({ subtitle: 'Form is invalid. Please check the fields.', title: 'Error' });
      return;
    }
    this.formGroup.updateValueAndValidity(); // Should not be needed if check is above

    const updatedData = this.formGroup.getRawValue();

    try {
      const claims = await this.httpService.post<AppAuthClaims>(Endpoints.Claims, updatedData);
      this.toastrService.success({ subtitle: `Los cambios se verán reflejados la próxima vez que ${updatedData.email} inicie sesión`, title: 'Éxito' });

      const currentUserEmail = this.userService.getUserSnapshot()?.email;
      if (currentUserEmail && updatedData.email && currentUserEmail === updatedData.email) {
        await this.userService.updateUserClaimsAndRefresh(claims);
      }
    } catch (error) {
      this.toastrService.error({ subtitle: 'Error updating user claims.', title: 'Error' });
      console.error('Error updating user claims:', error);
    }
  }

  public updateExpMonthsAndCalculate(inputSignal: WritableSignal<InputClaim>, newExpMonths: number | null): void {
    const currentInputValue = { ...inputSignal() }; // Get current value and clone it
    currentInputValue.expMonths = newExpMonths;
    if (newExpMonths !== null) {
      // if expMonths is set, exp should be calculated, otherwise user might set exp manually
      currentInputValue.exp = null; // Reset direct exp date if months are chosen
    }
    this.calculateExpireDate(currentInputValue); // This will modify currentInputValue.exp
    inputSignal.set(currentInputValue); // Update the signal with the new state
  }

  public calculateExpireDate(input: InputClaim): void {
    // input is now a regular object, not a signal
    const date = new Date();
    if (input.expMonths == null) {
      // If expMonths is null, don't automatically nullify input.exp
      // This allows manual setting of input.exp via calendar
      // input.exp = null;
    } else {
      input.exp = addMonths(date, input.expMonths);
    }
  }

  public updatePermissionInput(key: keyof InputClaim, value: any): void {
    this.permissionInput.update((current) => ({ ...current, [key]: value }));
  }

  public updateRolInput(key: keyof InputClaim, value: any): void {
    this.rolInput.update((current) => ({ ...current, [key]: value }));
  }

  public updatePlanInput(key: keyof InputClaim, value: any): void {
    this.planInput.update((current) => ({ ...current, [key]: value }));
  }

  public async deleteUser() {
    const emailToDelete = this.deleteUserForm();
    if (!emailToDelete) {
      this.toastrService.error({ subtitle: 'Email for deletion is not set.', title: 'Error' });
      return;
    }
    const isConfirmed = confirm(`¿Está seguro que desea eliminar el usuario ${emailToDelete}?`);
    if (isConfirmed) {
      try {
        await this.httpService.delete(`${Endpoints.DeleteUser}/${emailToDelete}`);
        this.toastrService.success({ subtitle: `El usuario ${emailToDelete} fue eliminado`, title: 'Éxito' });
        this.deleteUserForm.set(''); // Reset signal
        if (this.formGroup.get('email')?.value === emailToDelete) {
          this.resetForm(); // Reset form if the deleted user is the one currently loaded
        }
      } catch (error) {
        this.toastrService.error({ subtitle: `Error deleting user ${emailToDelete}`, title: 'Error' });
        console.error('Error deleting user:', error);
      }
    }
  }

  // Helper method to check if permissions controls are empty
  public isPermissionsEmpty(): boolean {
    const permissionsGroup = this.formGroup.get('permissions') as FormGroup;
    return !permissionsGroup || Object.keys(permissionsGroup.controls).length === 0;
  }

  // Helper method to check if an object is empty
  public isObjectEmpty(obj: object | null | undefined): boolean {
    // Check if obj is not null and is an object before getting keys
    return !obj || (typeof obj === 'object' && Object.keys(obj).length === 0);
  }
}
